import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { ArrowLeft, Bell, MessageSquare, Mail, Smartphone, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import PushAndQuietHoursCard from '../../components/PushAndQuietHoursCard';

const NotificationSettings = () => {
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/notifications/enhanced-preferences')
      .then(res => { setPrefs(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleToggle = async (key, value) => {
    const updated = { ...prefs, [key]: value };
    setPrefs(updated);
    setSaving(true);
    try {
      await api.put('/notifications/enhanced-preferences', updated);
      toast.success('Preferences saved');
    } catch {
      toast.error('Failed to save');
      setPrefs(prefs); // revert
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prefs) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="notification-settings-page">
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Bell className="w-6 h-6 text-cyan-600" />
          <h1 className="text-2xl font-bold text-gray-900">Notification Preferences</h1>
        </div>

        {/* Alert Types */}
        <Card className="mb-6 bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Alert Types</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {[
              { key: 'gig_alerts', label: 'Gig Alerts', desc: 'New gig opportunities matching your profile', icon: Megaphone },
              { key: 'application_updates', label: 'Application Updates', desc: 'Status changes on your applications (viewed, accepted, rejected)', icon: MessageSquare },
              { key: 'booking_confirmations', label: 'Booking Confirmations', desc: 'Confirmations when you are booked for a gig', icon: Bell },
              { key: 'platform_announcements', label: 'Platform Announcements', desc: 'Updates, new features, and important notices', icon: Megaphone },
            ].map(({ key, label, desc, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between" data-testid={`pref-${key}`}>
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{label}</p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                </div>
                <Switch
                  checked={prefs[key]}
                  onCheckedChange={(v) => handleToggle(key, v)}
                  data-testid={`toggle-${key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Delivery Channels */}
        <Card className="bg-white border-gray-200">
          <CardHeader>
            <CardTitle className="text-base">Delivery Channels</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {[
              { key: 'push_enabled', label: 'Push Notifications', desc: 'Browser and mobile push alerts', icon: Bell },
              { key: 'email_enabled', label: 'Email Notifications', desc: 'Receive alerts via email', icon: Mail },
              { key: 'sms_enabled', label: 'SMS Notifications', desc: 'Text message alerts for urgent updates', icon: Smartphone },
            ].map(({ key, label, desc, icon: Icon }) => (
              <div key={key} className="flex items-center justify-between" data-testid={`pref-${key}`}>
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{label}</p>
                    <p className="text-xs text-gray-500">{desc}</p>
                  </div>
                </div>
                <Switch
                  checked={prefs[key]}
                  onCheckedChange={(v) => handleToggle(key, v)}
                  data-testid={`toggle-${key}`}
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Iter-181 — Push subscription, per-event opt-in, quiet hours */}
        <div className="mt-6">
          <PushAndQuietHoursCard />
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
